import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Chip, 
  Card, 
  CardContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PeopleIcon from '@mui/icons-material/People';
import { clientData } from '../data/clientData';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[4],
}));

const ProjectCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));


const ClientDetail: React.FC = () => {
  const { clientSlug } = useParams<{ clientSlug: string }>();
  const navigate = useNavigate();
  
  const client = clientData[clientSlug || ''];

  if (!client) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        <Typography variant="h4">Client not found</Typography>
        <Typography variant="body1">Slug: {clientSlug}</Typography>
        <Typography variant="body1">Available clients: {Object.keys(clientData).join(', ')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/about')}
          sx={{ mb: 4 }}
        >
          Back to About
        </Button>

        {/* Header Section */}
        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Box
              component="img"
              src={client.logo}
              alt={`${client.name} logo`}
              sx={{
                height: 80,
                width: 'auto',
                maxWidth: 200,
                objectFit: 'contain',
                mr: 3,
                backgroundColor: '#ffffff',
                borderRadius: 1,
                padding: 1.5,
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            />
            <Box>
              <Typography variant="h3" component="h1" gutterBottom color="primary">
                {client.name}
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                {client.industry}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip icon={<ScheduleIcon />} label={client.duration} color="primary" />
                <Chip icon={<PeopleIcon />} label={client.teamSize} color="secondary" />
              </Box>
            </Box>
          </Box>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            {client.description}
          </Typography>
          
          <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
            {client.overview}
          </Typography>
        </StyledPaper>

        {/* Key Achievements */}
        <StyledPaper>
          <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ mb: 3 }}>
            Key Achievements
          </Typography>
          <Grid container spacing={2}>
            {client.achievements.map((achievement: string, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'rgba(25, 118, 210, 0.05)', borderRadius: 1 }}>
                  <CheckCircleIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="body1">{achievement}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </StyledPaper>

        {/* Projects */}
        <StyledPaper>
          <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ mb: 3 }}>
            Major Projects
          </Typography>
          <Grid container spacing={3}>
            {client.projects.map((project: any, index: number) => (
              <Grid item xs={12} md={6} key={index}>
                <ProjectCard>
                  <CardContent>
                    <Typography variant="h5" component="h3" gutterBottom color="primary">
                      {project.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {project.duration} • {project.team}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {project.description}
                    </Typography>
                    
                    <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                      Technologies Used:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                      {project.technologies.map((tech: string, techIndex: number) => (
                        <Chip key={techIndex} label={tech} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                    
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Key Achievements:
                    </Typography>
                    <List dense>
                      {project.achievements.map((achievement: string, achIndex: number) => (
                        <ListItem key={achIndex} sx={{ py: 0.5 }}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={achievement} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </ProjectCard>
              </Grid>
            ))}
          </Grid>
        </StyledPaper>

        {/* Technologies */}
        <StyledPaper>
          <Typography variant="h4" component="h2" gutterBottom color="primary" sx={{ mb: 3 }}>
            Technologies & Tools
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {client.technologies.map((tech: string, index: number) => (
              <Chip
                key={index}
                label={tech}
                color="primary"
                sx={{ fontSize: '1rem', padding: '8px 16px' }}
              />
            ))}
          </Box>
        </StyledPaper>

      </Container>
    </Box>
  );
};

export default ClientDetail;
